import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AmplitudeService } from '@inyova/inyova-shared';
import { Account, Customer, FeatureFlags, PortfolioAllocation, WithdrawalAllocation } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';
import { PageScrollService } from 'ngx-page-scroll-core';

import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as StrategyActions from '../../../strategy/strategy.actions';
import * as AppRootActions from '@app/app.actions';
import * as ProfileActions from '@profile/profile.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromProfile from '@profile/profile.reducers';
import * as fromStrategy from '@strategy/reducers';

import { MOBILE_VIEW_TRESHOLD } from '@app/app.constants';
import { FinancialSituationBankDetail } from '@shared/models/Account';
import { NewDocType } from '@shared/models/Profile';
import { CustomerInvestmentData } from '@shared/models/Strategy';
import { AccountOpeningService } from '@shared/services/account-opening.service';
import { ToastNotificationService } from '@shared/services/toast-notification.service';
import { TrackingService } from '@shared/services/tracking.service';

import { environment } from '../../../../../environments/environment';

export enum Tabs {
  SavingsPlan,
  Withdrawal,
  AccountDetails
}

@Component({
  selector: 'app-fund-withdraw',
  templateUrl: './fund-withdraw.component.html',
  styleUrls: ['./fund-withdraw.component.scss'],
  standalone: false
})
export class FundWithdrawComponent implements OnInit, OnDestroy {
  protected readonly environment = environment;
  protected readonly onDestroy$ = new Subject<void>();
  withdrawalAllocation$: Observable<WithdrawalAllocation>;
  hasWithdrawalPromotion$: Observable<boolean>;
  hasInvestDetailsFlag$: Observable<boolean>;
  accountInvested$: Observable<boolean>;
  customerInvestment$: Observable<CustomerInvestmentData>;
  customer$: Observable<Customer>;
  currentAccount$: Observable<Account>;
  portfolioAllocation$: Observable<PortfolioAllocation | null>;
  transfer3ADoc$: Observable<NewDocType[]>;
  kind: '3b' | '3a' | 'kid' | 'ci' | 'grow';
  activeTab: Tabs;
  financialDetails: null | { iban: number; bank: FinancialSituationBankDetail } = null;
  showSpTab = true;
  tabs = Tabs;

  @ViewChild('reasonsEl') reasonsEl: ElementRef;

  constructor(
    private store: Store,
    private trackingService: TrackingService,
    private translateService: TranslateService,
    private toastNotificationService: ToastNotificationService,
    private router: Router,
    private scrollService: PageScrollService,
    private route: ActivatedRoute,
    private accountOpeningService: AccountOpeningService,
    @Inject(DOCUMENT) private document: Document,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(StrategyActions.getCustomerInvestment());
    this.store.dispatch(AppRootActions.getPromotions());
    this.store.dispatch(StrategyActions.getLatestStrategyDataEve2());

    this.activeTab = this.tabs.SavingsPlan;

    this.customerInvestment$ = this.store.select(fromStrategy.selectCustomerInvestmentData);
    this.customer$ = this.store.select(fromAppRoot.selectCustomer);
    this.currentAccount$ = this.store.select(fromAppRoot.selectCurrentAccount);
    this.portfolioAllocation$ = this.store.select(fromStrategy.selectPortfolioAllocation);
    this.accountInvested$ = this.store.select(fromStrategy.selectAccountInvested);
    this.withdrawalAllocation$ = this.store.select(fromStrategy.selectWithdrawalAllocation);
    this.hasWithdrawalPromotion$ = this.store.select(fromAppRoot.selectWithdrawalPromotion);
    this.hasInvestDetailsFlag$ = this.store.select(fromAppRoot.selectCustomerFeatureFlag(FeatureFlags.INVEST_DETAILS));
    this.transfer3ADoc$ = this.store.select(fromProfile.selectTransfer3ADoc);

    this.store
      .select(fromAppRoot.selectCurrentAccount)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((account: Account) => {
        this.kind = account.kind;

        if (!account.created_at) {
          return;
        }

        if (account.kind === '3a') {
          this.store.dispatch(StrategyActions.getRiskQuestions());
          this.showSpTab = account.max_contribution_level !== 'transfer_only';
          if (!this.showSpTab) {
            this.activeTab = Tabs.AccountDetails;
          }
        }
      });

    const showReasons: string | null = this.route.snapshot.queryParamMap.get('reasons');
    const showAccountDetails: string | null = this.route.snapshot.queryParamMap.get('accountDetails');
    const showRemind: string | null = this.route.snapshot.queryParamMap.get('remind');
    const showTab: string | null = this.route.snapshot.queryParamMap.get('tab');

    if (showReasons || showAccountDetails || showRemind || showTab) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { reasons: null, accountDetails: null, remind: null, tab: null },
        queryParamsHandling: 'merge'
      });

      if (showRemind) {
        this.trackingService.trackActivity('[Deeplink] Reminder received and link clicked');
      }

      if (showAccountDetails) {
        this.activeTab = Tabs.AccountDetails;
        return;
      }

      if (showTab) {
        this.activeTab = this.setActiveTab(showTab);
        return;
      }

      if (showReasons) {
        this.scrollToReasons();
      }
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onSwitchTab(selectedTab: number) {
    if (selectedTab === Tabs.SavingsPlan || selectedTab === Tabs.AccountDetails) {
      this.amplitudeService.trackEvent(`${selectedTab === Tabs.SavingsPlan ? 'Savings Plan' : 'Account Details'} Opened`, { kind: this.kind });
    }
    this.trackingService.trackActivity(`[Button] Fund/Withdraw: Select tab ${Tabs[selectedTab]}`);
    this.activeTab = selectedTab;
  }

  onGetTransfer3ADoc() {
    this.store.dispatch(ProfileActions.fetchCustomerDocs());
  }

  scrollToReasons() {
    if (this.activeTab !== Tabs.SavingsPlan) {
      this.activeTab = Tabs.SavingsPlan;
    }
    setTimeout(() => {
      this.scrollService.scroll({
        document: this.document,
        scrollTarget: this.reasonsEl.nativeElement,
        duration: 500
      });
    }, 200);
  }

  onScrollToTop() {
    if (window.innerWidth > MOBILE_VIEW_TRESHOLD) {
      return;
    }
    setTimeout(() => {
      this.scrollService.scroll({ document: this.document, scrollTarget: '.withdrawal-card', duration: 0 });
    }, 0);
  }

  onContactSupport() {
    this.trackingService.trackActivity('[Link] Account details: Open help');
    void this.router.navigate(['/help']);
  }

  onGetFinancialSituation() {
    this.accountOpeningService.getFinancialSituation().subscribe(({ financial_detail }) => {
      this.financialDetails = {
        iban: financial_detail.customer_iban,
        bank: financial_detail.bank_detail
      };
    });
  }

  onGetWithdrawalData() {
    this.store.dispatch(StrategyActions.getWithdrawalData());
  }

  onReFetchPromotions() {
    this.store.dispatch(AppRootActions.getPromotions(true));
  }

  onShowToastError(errorKey: string) {
    this.toastNotificationService.show('critical', this.translateService.instant(errorKey));
  }

  onTrackActivity(activity: string) {
    this.trackingService.trackActivity(activity);
  }

  setActiveTab(tab: string): Tabs {
    switch (tab) {
      case 'withdrawal':
        return Tabs.Withdrawal;
      case 'transfer3A':
        return Tabs.AccountDetails;
      case 'oneOffInvestment':
      case 'savingsPlan':
        return Tabs.SavingsPlan;
      default:
        return Tabs.SavingsPlan;
    }
  }
}
